import { Reorder } from "framer-motion";
import { useEffect, useState } from "react";
import { GripVerticalIcon } from "lucide-react";

import { TableColumnType } from "@/components/@types";
import { HeaderIndicesType } from "@/components/organisms/CSV";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

export function CSVColumnMatcher({
  expectedHeaders,
  fileHeaders,
  headerIndicesState,
  initialOrder,
}: {
  expectedHeaders: TableColumnType[];
  fileHeaders: string[];
  headerIndicesState: [
    HeaderIndicesType,
    (headerIndices: HeaderIndicesType) => void,
  ];
  /**
   * Optional pre-arranged ordering of items in the right-hand column.
   * When provided, items at index ``i`` are visually paired with
   * ``expectedHeaders[i]`` — letting callers seed a smart/auto-detected
   * mapping. Any entry that is not also in ``fileHeaders`` is treated as
   * an unmatched placeholder (use a string like ``"No match-<i>"``).
   *
   * When omitted the legacy behaviour is preserved: file headers appear
   * in their original CSV order, padded with placeholders if there are
   * fewer file columns than expected fields.
   */
  initialOrder?: string[];
}) {
  const [, setHeaderIndices] = headerIndicesState;

  const computeInitialItems = (): string[] => {
    if (initialOrder && initialOrder.length > 0) {
      return initialOrder;
    }
    const maxItems = Math.max(expectedHeaders.length, fileHeaders.length);
    const items: string[] = [];
    for (let i = 0; i < maxItems; i++) {
      if (i < fileHeaders.length) {
        items.push(fileHeaders[i]);
      } else {
        items.push(`No match-${i}`);
      }
    }
    return items;
  };

  const [reorderedItems, setReorderedItems] = useState(computeInitialItems);

  const makePlaceholder = (i: number) =>
    // Suffix with a random token so framer-motion's ``Reorder.Item``
    // keys stay unique even when the user skips multiple rows in a row.
    `No match-${i}-${Math.random().toString(36).slice(2, 8)}`;

  // Apply a per-row dropdown choice: pair ``fileHeader`` with the
  // expected row at ``expectedIndex``. We mutate ``reorderedItems``
  // in place (with a swap or a placeholder insert) so the existing
  // index ⇄ expected-header pairing logic in the effect above keeps
  // working unchanged.
  const handleSelectFileHeader = (
    expectedIndex: number,
    fileHeader: string,
  ) => {
    setReorderedItems((prev) => {
      const next = [...prev];
      if (fileHeader === "__skip__") {
        const removed = next[expectedIndex];
        next[expectedIndex] = makePlaceholder(expectedIndex);
        if (removed && !removed.includes("No match")) {
          next.push(removed);
        }
        return next;
      }
      const currentPos = next.indexOf(fileHeader);
      if (currentPos === -1 || currentPos === expectedIndex) return prev;
      const tmp = next[expectedIndex];
      next[expectedIndex] = next[currentPos];
      next[currentPos] = tmp;
      return next;
    });
  };

  useEffect(() => {
    const newHeaderIndices = {};
    reorderedItems.forEach((item, index) => {
      // Find expected header at the same index as the item
      const expectedHeader = expectedHeaders[index];
      if (expectedHeader) {
        newHeaderIndices[expectedHeader.objectKey] = fileHeaders.indexOf(item);
      }
    });
    setHeaderIndices(newHeaderIndices);
  }, [reorderedItems]);

  return (
    <div className="flex min-w-0 pb-2 justify-between flex-nowrap">
      {/* Expected columns */}
      <div className="flex min-w-0 flex-col flex-nowrap w-[calc(50%-2px)]">
        <Typography level="title-md" className="mb-1">
          Expected columns
        </Typography>
        {expectedHeaders.map((header, index) => {
          const currentItem = reorderedItems[index];
          const currentValue =
            currentItem && !currentItem.includes("No match")
              ? currentItem
              : "__skip__";
          return (
            <div
              key={index}
              className="min-w-0 p-2 mt-1 [outline:1px_solid_var(--border)] [outline-offset:-1px] h-11 flex items-center gap-2 bg-background flex-nowrap justify-between relative rounded-[4px]"
            >
              <Typography className="truncate">{header.label}</Typography>
              {/*
                Per-row picker as a no-drag alternative. Native
                ``<select>`` keeps the dependency surface small (no Joy
                ``Select`` is in use elsewhere in the codebase) while
                still giving keyboard / accessibility support out of
                the box. Picking a column here triggers the same swap
                framer-motion's drag does — the two affordances stay in
                sync because both write to ``reorderedItems``.
              */}
              <select
                value={currentValue}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  handleSelectFileHeader(index, e.target.value)
                }
                className="[font:inherit] text-foreground [background:transparent] [border:1px_solid_var(--border)] rounded-[4px] pl-1 pr-1 pt-0.5 pb-0.5 max-w-[55%] cursor-pointer"
              >
                <option value="__skip__">(skip)</option>
                {fileHeaders.map((fh) => (
                  <option key={fh} value={fh}>
                    {fh}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>

      {/* File columns */}
      <div className="flex min-w-0 flex-wrap flex-col w-[calc(50%-2px)] ml-0">
        <Typography level="title-md" className="mb-1">
          CSV Columns
        </Typography>
        <Reorder.Group
          axis="y"
          values={reorderedItems}
          onReorder={(newOrder) => {
            // if any items are 'No column match' and their index is greater than the number of expected headers, remove them
            newOrder = newOrder.filter((item, index) => {
              return !(
                item.includes("No match") && index >= expectedHeaders.length
              );
            });
            setReorderedItems(newOrder);
          }}
          style={{
            // marginTop: "-1px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            width: "100%",
            // height: "100%",
            marginTop: "0px",
            marginBottom: "0px",
            marginLeft: "0px",
            paddingLeft: "0px",
          }}
        >
          {reorderedItems.map((key, index) => {
            const isOverflowItem = index > expectedHeaders.length - 1;
            return (
              <div
                key={key}
                // mt: "-1px",
                className="min-w-0 h-11 flex-nowrap flex items-center justify-between mt-1 hover:cursor-grab"
              >
                <Item
                  item={key}
                  isOverflowItem={isOverflowItem}
                  onRemove={() => {
                    const newItems = [...reorderedItems];
                    const removedItem = newItems[index];
                    newItems.push(removedItem);
                    newItems[index] = makePlaceholder(index);
                    setReorderedItems(newItems);
                  }}
                />
              </div>
            );
          })}
        </Reorder.Group>
      </div>
    </div>
  );
}

export const Item = ({ item, isOverflowItem, onRemove }) => {
  const header = item?.includes("No match") ? null : item;
  const isUnmatched = item?.includes("No match");
  return (
    <Reorder.Item
      value={item}
      id={item}
      style={{
        listStyleType: "none",
        height: "100%",
        width: "100%",
        display: "flex",
        flexWrap: "nowrap",
        alignItems: "center",
        justifyContent: "space-between",
        paddingLeft: "4px",
        paddingRight: "4px",
        backgroundColor: "var(--background)",
        outline: "1px solid var(--border)",
        outlineOffset: -1,
        borderRadius: "4px",
      }}
    >
      <GripVerticalIcon
        aria-hidden
        focusable="false"
        data-testid="DragIndicatorIcon"
        // As Material's icon drew under Joy's theme: Joy's icon colour (its
        // "info" is a colour Joy has not got), at the size of the words
        // around it.
        className="inline-block size-[1em] shrink-0 fill-current stroke-none [stroke-width:1px] select-none text-muted-foreground transform-[scale(0.8)_rotate(90deg)] mr-2"
      />
      <Typography
        className={cn(
          isUnmatched ? "text-muted-foreground/80" : "text-foreground",
        )}
      >
        {header ? header : "No matching column..."}
      </Typography>
      <div className="grow" />
      {header && !isOverflowItem && (
        <Button
          variant="outline"
          size="xs"
          onClick={onRemove}
          className="text-destructive hover:text-destructive"
        >
          Remove
        </Button>
      )}
    </Reorder.Item>
  );
};
